import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  createMuiTheme,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Select from "react-select";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteProvincia,
  getProvinciaById,
  postProvincia,
  updateProvincia,
} from "../../../../api/provincia";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { getPaises } from "../../../../api/pais";
import { shallowEqual, useSelector } from "react-redux";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyProvincia() {
  return {
    fullName: "",
    pais: "",
  };
}

export default function EditProvinciasPage() {
  const user = useSelector((store) => store.authentication?.user, shallowEqual);
  const [provincia, setProvincia] = useState(getEmptyProvincia());
  const [paises, setPaises] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const provinciaId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    getPaises()
      .then((res) => {
        if (res.status === 200) {
          setPaises(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener las paises.",
        });
        history.push("/provincias");
      });
    if (!provinciaId) {
      disableLoadingData();
      return;
    }
    getProvinciaById(provinciaId)
      .then((res) => {
        if (res.status === 200) {
          setProvincia(res.data);
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se ha podido obtener la província.",
        });
        history.push("/provincias");
      });
  }, [provinciaId, disableLoadingData, history]);

  function saveProvincia() {
    if (!provinciaId) {
      postProvincia(provincia)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "¡Guardado!",
              customMessage: "Província creada correctamente.",
            });
            history.push("/provincias");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se ha podido guardar la província.",
          });
        });
    } else {
      updateProvincia(provinciaId, provincia)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "¡Guardado!",
              customMessage: "Los cambios se han guardado correctamente.",
            });
            history.push("/provincias");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se han podido guardar los cambios.",
          });
        });
    }
  }

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleChange = (element) => (event) => {
    if (element === "pais") {
      setProvincia({ ...provincia, [element]: event._id });
    } else setProvincia({ ...provincia, [element]: event.target.value });
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/provincias"),
            onSave: saveProvincia,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!provinciaId,
            setObj: setProvincia,
            obj: provincia,
          })}
          <CardHeader title="Editar província"></CardHeader>
          <CardBody>
            <TextField
              id={`fullName`}
              label="Província"
              value={provincia.fullName}
              onChange={handleChange("fullName")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              required
            />
            <span>Pais</span>
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              className="my-2"
              defaultValue={
                provincia.pais && paises
                  ? paises.find((item) => item._id === provincia.pais)
                  : ""
              }
              onChange={handleChange("pais")}
              options={paises}
              getOptionLabel={(option) => `${option.name}`}
              getOptionValue={(option) => `${option._id}`}
              placeholder="Seleccione una pais..."
              styles={{
                // Fixes the overlapping problem of the component
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
            />
          </CardBody>
        </Card>
        {provinciaId && user?.role === "admin" && (
          <>
            <ConfirmDialog
              title={"¿Estás seguro de que quieres eliminar esta provincia?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteProvincia(provinciaId)
                  .then((res) => {
                    if (res.status === 204) {
                      alertSuccess({
                        title: "¡Eliminada!",
                        customMessage: "Provincia eliminada correctamente.",
                      });
                      history.push("/provincias");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "No se ha podido eliminar la provincia.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
